import { useState, useRef } from "react"
import { toast } from "sonner"

import { getAptSearchAPI } from "@/apis/apt-api"
import { langStore } from "@/stores/lang-store"
import useOutsideClick from "@/hooks/use-outside-click"

import AptAddressDropdown from "./apt-address-dropdown"
import SearchFilter from "../../_components/search-filter"
import { aptSelectBundle } from "../apt-select-const"
import { useToggleStore } from "@/hooks/use-toggle-store"
import { aptGroupStore } from "./apt-group-store"

const AptSearchView = ({ addrTree, onSearchResults }) => {
  const { lang } = langStore()
  const filterRef = useRef(null)

  const [tempAddr, setTempAddr] = useState({
    sido: "",
    sgg: "",
    road: ""
  })

  const [tempKeyword, setTempKeyword] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const { show: isEdit } = useToggleStore("group-edit-mode")

  const regionViewer = () => {
    let text = ""

    text += tempAddr.sido ? tempAddr.sido + " " : ""
    text += tempAddr.sgg ? tempAddr.sgg + " " : ""
    text += tempAddr.road ? tempAddr.road + " " : ""

    if (!text) {
      return aptSelectBundle.filter.regionPlaceholder[lang]
    }

    return text
  }

  const toggleOpen = () => {
    setIsOpen((prev) => !prev)
  }

  useOutsideClick({
    ref: filterRef,
    enabled: isOpen,
    onOutside: () => setIsOpen(false)
  })

  const getSidoList = () => {
    if (!addrTree) return []

    return Object.keys(addrTree)
  }

  const getSggList = () => {
    if (tempAddr.sido === "전체" || !tempAddr.sido) return []

    return Object.keys(addrTree?.[tempAddr.sido] ?? {})
  }

  const getRoadList = () => {
    if (tempAddr.sido === "전체" || tempAddr.sgg === "전체" || !tempAddr.sido) {
      return []
    }

    if (tempAddr.sido && tempAddr.sgg) {
      return Object.keys(addrTree?.[tempAddr.sido]?.[tempAddr.sgg] ?? {})
    }

    return []
  }

  const handleReset = () => {
    setTempAddr({
      sido: "",
      sgg: "",
      road: ""
    })
  }

  const collectAddrCodes = (node, result = []) => {
    if (Array.isArray(node)) {
      result.push(...node)
      return result
    }

    if (!node || typeof node !== "object") return result

    for (const key in node) {
      collectAddrCodes(node[key], result)
    }

    return result
  }

  const handleSearch = async () => {
    let node = addrTree

    if (!node) return

    if (tempAddr.sido && tempAddr.sido !== aptSelectBundle.filter.all[lang]) {
      node = node[tempAddr.sido]
    }

    if (tempAddr.sgg && tempAddr.sgg !== "전체") {
      node = node?.[tempAddr.sgg]
    }

    if (tempAddr.road && tempAddr.road !== "전체") {
      node = node?.[tempAddr.road]
    }

    if (!node) {
      toast.error("선택한 주소에 해당하는 아파트가 없습니다.")
      return
    }

    const addr_codes = collectAddrCodes(node)

    try {
      const result = await getAptSearchAPI({
        addr_codes,
        keyword: tempKeyword
      })

      onSearchResults(result)
      !isEdit && aptGroupStore.getState().setActiveGroupName(null)
      setIsOpen(false)

      if (result.length === 0) {
        toast.error("검색된 아파트가 없습니다.")
      }
    } catch {
      toast.error("아파트 검색에 실패했습니다.")
    }
  }

  return (
    <section className="relative mb-2.5" ref={filterRef}>
      <SearchFilter
        preview={regionViewer()}
        keyword={tempKeyword}
        setKeyword={setTempKeyword}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        placeholder={aptSelectBundle.filter.inputPlaceholder[lang]}
        onSearch={handleSearch}
      />

      {isOpen && (
        <AptAddressDropdown
          sidoList={getSidoList()}
          sggList={getSggList()}
          roadList={getRoadList()}
          tempAddr={tempAddr}
          setTempAddr={setTempAddr}
          onSearch={handleSearch}
          onReset={handleReset}
        />
      )}
    </section>
  )
}

export default AptSearchView
